import { useEffect } from 'react'

import { useQuery } from '@apollo/client'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { GetBrandStyleIdentifierDocument } from '__generated__/atlas-types'

import { atlasClient } from 'graphql/client'
import {
  allowEditStylesImage as allowEditStylesImageSelector,
  isAccountTypeRetailer,
  restrictAddEditLinesheet as isAddEditLinesheetRestrictedSelector,
  isAddOrCreateStylesRestricted as isAddOrCreateStylesRestrictedSelector,
  isCoreBrand as isCoreBrandSelector,
  isEditStylesRestricted as isEditStylesRestrictedSelector,
  isLiteBrand as isLiteBrandSelector,
  isProBrand as isProBrandSelector,
} from 'store/currentUser/selectors'

import { LINESHEETS, UPLOAD_DATA } from 'routes/paths'

const MANAGE_LINESHEETS_TITLE_ID =
  '#sub-navbar-tab-list-linesheets__list-view__manage-button'
const MANAGE_LINESHEETS_ONLY_BULK_ID =
  'manage-linesheets__pendo-id__only-bulk-image'
const MANAGE_LINESHEETS_BOTH_UPLOADER_ID =
  'manage-linesheets__pendo-id__both-image-and-style-uploader'
const UPLOAD_DATA_TITLE_ID =
  '#upload-data__upload-data-form__upload-data-header'
const UPLOAD_DATA_ONLY_BULK_ID = 'upload-data__pendo-id__only-bulk-image'
const UPLOAD_DATA_BOTH_UPLOADER_ID =
  'upload-data__pendo-id__both-image-and-style-uploader'

export const useImportStylesExperience = (
  styleIdentifiers: string[] | null,
  loading: boolean,
) => {
  const {
    basicStyleUploader,
    earlyAccessBasicStyleUploader,
    earlyAccessBasicStyleUploaderAdmin,
  } = useFlags()

  const {
    isRetailer,
    isCoreBrand,
    isLiteBrand,
    isProBrand,
    canEditStyles,
    canAddOrCreateStyles,
    canAddOrEditLinesheets,
  } = useAccountTypeAndPermissions()

  if (isRetailer || loading) {
    return { isEnabled: false, loading }
  }

  // Core brands
  if (isCoreBrand) {
    // Style uploader is always enabled for core brands
    return { isEnabled: true, loading }
  }

  // Lite brands
  if (isLiteBrand) {
    // If the admin flag is enabled, the style uploader will depend on the early access FF
    // If the admin flag is disabled, the style uploader will default to true
    const isEnabled = earlyAccessBasicStyleUploaderAdmin
      ? earlyAccessBasicStyleUploader
      : true
    return { isEnabled, loading }
  }

  // Pro brands
  if (isProBrand) {
    // If the user doesn't have permission, return false
    if (!canEditStyles || !canAddOrCreateStyles || !canAddOrEditLinesheets) {
      return { isEnabled: false, loading }
    }

    // If the style number is the only style identifier, return true
    if (isOnlyStyleNumberIdentifier(styleIdentifiers)) {
      return { isEnabled: true, loading }
    }

    // The feature will also be handled through a FF to give access to some brands
    // that don't have the style number as the only style identifier
    return { isEnabled: basicStyleUploader, loading }
  }

  // For any other cases, return false
  return { isEnabled: false, loading }
}

export const useGetBrandStyleIdentifier = (isRetailer?: boolean) => {
  const { data, loading } = useQuery(GetBrandStyleIdentifierDocument, {
    client: atlasClient,
    skip: isRetailer,
  })

  useEffect(() => {
    if (!loading && window.pendo) {
      const styleIdentifiersCombined = (
        data?.getBrandStyleIdentifier.styleIdentifiers || []
      ).join(',')
      window.pendo.updateOptions({
        account: { styleIdentifiers: styleIdentifiersCombined },
      })
    }
  }, [data?.getBrandStyleIdentifier.styleIdentifiers, loading])

  return {
    styleIdentifiers: data?.getBrandStyleIdentifier.styleIdentifiers as
      | string[]
      | null,
    loading,
  }
}

const isOnlyStyleNumberIdentifier = (
  styleIdentifiers?: string[] | null,
): boolean => {
  if (!Array.isArray(styleIdentifiers)) {
    return false
  }

  if (styleIdentifiers.length === 1) {
    return styleIdentifiers[0] === 'style_number'
  }

  return false
}

export const useBulkImageUploaderExperience = () => {
  const {
    isRetailer,
    isCoreBrand,
    isLiteBrand,
    isProBrand,
    canEditStyleImages,
    canEditStyles,
  } = useAccountTypeAndPermissions()

  if (isRetailer) {
    return false
  }

  // Core brands
  if (isCoreBrand || isLiteBrand) {
    // Image uploader is always enabled for core and lite brands
    return true
  }

  // Pro Brands
  if (isProBrand) {
    // If the user doesn't have permission, return false
    if (!canEditStyleImages && !canEditStyles) {
      return false
    } else {
      return true
    }
  }

  // Default to false
  return false
}

export const useSetPendoIds = (
  isBulkImageUploaderEnabled: boolean,
  isImportStylesEnabled: boolean,
  loading: boolean,
) => {
  const location = useLocation()

  if (loading) {
    return null
  }

  if (
    (location.pathname !== LINESHEETS && location.pathname !== UPLOAD_DATA) ||
    !isBulkImageUploaderEnabled
  ) {
    return null
  }

  const observerCallback = () => {
    const isLinesheetsPage = location.pathname === LINESHEETS
    const elementId = isLinesheetsPage
      ? MANAGE_LINESHEETS_TITLE_ID
      : UPLOAD_DATA_TITLE_ID
    const element = document.querySelector(elementId)
    if (element) {
      if (isBulkImageUploaderEnabled && !isImportStylesEnabled) {
        element.setAttribute(
          'id',
          isLinesheetsPage
            ? MANAGE_LINESHEETS_ONLY_BULK_ID
            : UPLOAD_DATA_ONLY_BULK_ID,
        )
      } else if (isBulkImageUploaderEnabled && isImportStylesEnabled) {
        element.setAttribute(
          'id',
          isLinesheetsPage
            ? MANAGE_LINESHEETS_BOTH_UPLOADER_ID
            : UPLOAD_DATA_BOTH_UPLOADER_ID,
        )
      }
      observer.disconnect()
    }
  }

  const observer = new MutationObserver(observerCallback)
  observer.observe(document, {
    childList: true,
    subtree: true,
  })

  return null
}

const useAccountTypeAndPermissions = () => {
  return {
    isRetailer: useSelector(isAccountTypeRetailer),
    isCoreBrand: useSelector(isCoreBrandSelector),
    isLiteBrand: useSelector(isLiteBrandSelector),
    isProBrand: useSelector(isProBrandSelector),
    canEditStyleImages: useSelector(allowEditStylesImageSelector),
    canEditStyles: !useSelector(isEditStylesRestrictedSelector),
    canAddOrCreateStyles: !useSelector(isAddOrCreateStylesRestrictedSelector),
    canAddOrEditLinesheets: !useSelector(isAddEditLinesheetRestrictedSelector),
  }
}
